import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Sparkles } from 'lucide-react';

const UNITY_IMG = "https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8Y29tbXVuaXR5JTIwc2VydmljZXxlbnwwfHwwfHx8MA%3D%3D";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.15 }
  })
};

export default function MissionVision() {
  return (
   <section id="mission" className="py-16 sm:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="text-center mb-20"
        >
          <motion.p variants={fadeUp} custom={0} className="text-primary font-medium tracking-[0.2em] uppercase text-sm mb-4">
            Who We Are
          </motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-foreground">
            Empowering Communities.{' '}
            <span className="text-primary italic">Transforming Lives.</span>
          </motion.h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image side */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative w-full h-full"
          >
            <div className="rounded-2xl overflow-hidden shadow-2xl w-full h-full">
              <img src={UNITY_IMG} alt="Community unity" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-secondary text-white rounded-2xl p-6 shadow-xl hidden sm:block">
              <p className="text-3xl font-heading font-bold">1,400+</p>
              <p className="text-sm opacity-90">Lives Impacted</p>
            </div>
          </motion.div>

          {/* Content side */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-8"
          >
            <motion.div variants={fadeUp} custom={0} className="flex gap-5">
              <div className="flex-shrink-0 w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
                <Target className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-2">Our Mission</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Rose of Jericho Community Development Center is a grassroots 501(c)(3) organization serving as a trusted anchor in our local and surrounding communities. We empower individuals and drive neighborhood revitalization by delivering programs in economic development, vocational and professional training, life skills, and health and wellness education, building the foundation for stronger, more resilient communities.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} custom={1} className="flex gap-5">
              <div className="flex-shrink-0 w-14 h-14 bg-secondary/10 rounded-xl flex items-center justify-center">
                <Eye className="w-7 h-7 text-secondary" />
              </div>
              <div>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-2">Our Vision</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We envision thriving, self-sustaining communities where every individual has the resources, education, and support to reach their full potential. Through intentional partnerships and integrated programming, Rose of Jericho Community Development Center is building a replicable model for lasting community transformation, one person, one family, one neighborhood at a time.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} custom={2} className="flex gap-5">
              <div className="flex-shrink-0 w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-accent" />
              </div>
              <div>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-2">Our Values</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Compassion, integrity, collaboration, and sustainability guide everything we do. We believe
                  that lasting change comes through trust, partnership, and the collective power of community.
                </p>
                </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
